import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { Heading1 } from '@/components/common/Heading1';
import { DateInput } from '@/components/DateInput';
import { Button } from '@/components/ui/button';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { dateOfBirthSchema } from '@/lib/validation-schemas';

const formId = 'verify-dob-form';
const formSchema = z.object({
  dateOfBirth: dateOfBirthSchema,
});

type VerifyDobFormData = z.infer<typeof formSchema>;

interface SelfScheduleVerifyDobStepProps {
  onSubmit: (dob: string) => void | Promise<void>;
  isPending: boolean;
  error?: string;
}

export const SelfScheduleVerifyDobStep = ({
  onSubmit,
  isPending,
  error,
}: SelfScheduleVerifyDobStepProps) => {
  const form = useForm<VerifyDobFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      dateOfBirth: '',
    },
  });

  const handleSubmit = (data: VerifyDobFormData) => {
    return onSubmit(data.dateOfBirth);
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="space-y-2">
        <p className="text-base">Welcome</p>
        <Heading1 className="mb-0 sm:mb-0">Time to book your virtual visit</Heading1>
      </div>

      <p>For security purposes, please confirm the patient's birthdate.</p>

      <form onSubmit={form.handleSubmit(handleSubmit)} id={formId} className="flex flex-col gap-8">
        <FieldGroup>
          <Controller
            name="dateOfBirth"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={`${formId}-dob`}>Patient date of birth</FieldLabel>
                <DateInput {...field} id={`${formId}-dob`} aria-invalid={fieldState.invalid} />
                {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
              </Field>
            )}
          />
        </FieldGroup>

        {error && <FieldError>{error}</FieldError>}

        <Button type="submit" disabled={isPending} className="w-full">
          {isPending ? 'Verifying...' : 'Continue'}
        </Button>
      </form>
    </div>
  );
};
